import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type { User } from 'firebase/auth';

import { changePasswordWithReauthentication, getPasswordChangeErrorKey } from '@/firebase/account-actions';
import { hasPasswordProvider } from '@/firebase/auth-policy';

interface PasswordSettingsProps {
  user: User | null;
}

export function PasswordSettings({ user }: PasswordSettingsProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmedPassword, setConfirmedPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  if (!user || !hasPasswordProvider(user)) {
    return null;
  }

  const clearForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmedPassword('');
    setError(null);
  };

  const handleClose = () => {
    if (isSaving) return;
    setIsOpen(false);
    clearForm();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (newPassword.length < 6) {
      setError(t('profile.passwordRequirements'));
      return;
    }

    if (newPassword !== confirmedPassword) {
      setError(t('profile.passwordMismatch'));
      return;
    }

    setIsSaving(true);
    try {
      await changePasswordWithReauthentication(user, currentPassword, newPassword);
      clearForm();
      setIsOpen(false);
      setSuccess(t('profile.passwordUpdated'));
    } catch (changeError) {
      setError(t(getPasswordChangeErrorKey(changeError)));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Divider sx={{ my: 3 }} />
      <Box sx={{ textAlign: 'left' }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('profile.security')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          {t('profile.securityDescription')}
        </Typography>
        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        <Button
          variant="outlined"
          fullWidth
          startIcon={<LockOutlinedIcon />}
          onClick={() => {
            setSuccess(null);
            setIsOpen(true);
          }}
        >
          {t('profile.changePassword')}
        </Button>
      </Box>

      <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="xs">
        <Box component="form" onSubmit={handleSubmit}>
          <DialogTitle>{t('profile.changePassword')}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {t('profile.passwordRequirements')}
            </DialogContentText>
            <Stack spacing={2}>
              <TextField
                autoFocus
                required
                fullWidth
                type="password"
                label={t('profile.currentPassword')}
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                autoComplete="current-password"
                disabled={isSaving}
              />
              <TextField
                required
                fullWidth
                type="password"
                label={t('profile.newPassword')}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                autoComplete="new-password"
                disabled={isSaving}
                inputProps={{ minLength: 6 }}
              />
              <TextField
                required
                fullWidth
                type="password"
                label={t('profile.confirmNewPassword')}
                value={confirmedPassword}
                onChange={(event) => setConfirmedPassword(event.target.value)}
                autoComplete="new-password"
                disabled={isSaving}
                error={Boolean(error)}
              />
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button type="button" onClick={handleClose} disabled={isSaving}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSaving || !currentPassword || !newPassword || !confirmedPassword}
              startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {t('profile.updatePassword')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
}
